import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { RadioButton } from 'primereact/radiobutton';
import { useFormik } from 'formik';
import classNames from 'classnames';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { handleGetRequest } from '../../services/GetTemplate';
import { useDispatch } from 'react-redux';
import { handlePostRequest } from '../../services/PostTemplate';

function AddUserDialog({ onHideUserDialog, dialogData, editable, getUsers }) {
    const [rolesList, setRolesList] = useState();
    const [initializing, setInitializing] = useState(false);
    const [zonesList, setZonesList] = useState();

    const dispatch = useDispatch();

    const formik = useFormik({
        initialValues: {
            id: '',
            fullName: '',
            roleID: '',
            status: true,
            cnic: '',
            mobileNumber: '',
            email: '',
            password: '',
            zoneId: '' || null
        },
        validate: (data) => {
            const errors = {};
            if (!data?.cnic) {
                errors.cnic='This field is required'
            }
            if (!data?.fullName) {
                errors.fullName = 'This field is required';
            }
            if (data.cnic && String(data.cnic).trim() !== '') {
                const cnicDigits = String(data.cnic).replace(/\D+/g, '');
                if (!/^\d{13}$/.test(cnicDigits)) errors.cnic = 'CNIC must be 13 digits.';
            }

            if (!data?.mobileNumber) {
                errors.mobileNumber='This field is required'
            }
            if (data.mobileNumber && String(data.mobileNumber).trim() !== '') {
                const mobileDigits = String(data.mobileNumber).replace(/\D+/g, '');
                if (mobileDigits.length !== 11) errors.mobileNumber = 'Mobile number must be 11 digits.';
                else if (!mobileDigits.startsWith('03')) errors.mobileNumber = 'Mobile number must start with 03.';
            }
            if (!data?.email) {
                errors.email='This field is required'
            }

            if (data.email && String(data.email).trim() !== '') {
                const emailRe = /^\S+@\S+\.\S+$/;
                if (!emailRe.test(String(data.email))) errors.email = 'Enter a valid email.';
            }

            if (data.status === null || data.status === undefined) errors.status = 'Status is required.';
            if (!data?.roleID) {
                errors.roleID='This field is required'
            }
            if (data?.roleID === 2) {
                if (!data?.zoneId) {
                    errors.zoneId = 'Zone is required';
                }
            }
            return errors;
        },
        onSubmit: async (data) => {
            if (!editable) {
                delete data['id'];
                const response = await dispatch(handlePostRequest(data, 'User/AddUserP', true, true));
                if (response?.success === true) {
                    onHideUserDialog();
                    getUsers();
                }
            } else {
                const response = await dispatch(handlePostRequest(data, 'User/Update', true, true));
                if (response?.success === true) {
                    onHideUserDialog();
                    getUsers();
                }
            }
        }
    });

    useEffect(() => {
        if (dialogData) {
            // Reset the whole form with dialog values to clear previous errors/touched state.
            // const zoneValue = zonesList?.filter((item) => dialogData?.zoneCode ===  item?.zoneCode)

            formik.resetForm({
                values: {
                    id: dialogData?.userId || '',
                    fullName: dialogData?.fullName || '',
                    roleID: dialogData?.roleID || '',
                    status: dialogData?.isActive !== undefined ? dialogData.isActive : true,
                    cnic: dialogData?.cnic || '',
                    mobileNumber: dialogData?.mobileNumber || '',
                    email: dialogData?.email || '',
                    password: '',
                    // zoneId: Array.isArray(zoneValue) && zoneValue.length > 0 ? zoneValue[0].zoneID : ''
                    zoneId: dialogData?.zoneId
                }
            });

            // stop showing validation messages that were triggered on mount
            setTimeout(() => setInitializing(false), 0);
        } else {
            // not editing an existing user: reset to initial form state
            formik.resetForm();
            setInitializing(false);
        }
    }, [dialogData]);

    // When dialog opens in edit mode, start in initializing state until dialogData is applied
    useEffect(() => {
        if (editable) {
            setInitializing(true);
        } else {
            setInitializing(false);
        }
    }, [editable]);

    const getRoles = async () => {
        const response = await handleGetRequest('DropDown/getRoleName', false);
        if (response?.success === true) {
            setRolesList(response?.data);
        }
    };

    useEffect(() => {
        getRoles();
    }, []);

    // Show an error if the field has been touched, or the user tried to submit,
    // or the user has entered something in the field but it doesn't match validation (helps show format hints while typing).
    const isFormFieldValid = (name) => {
        if (initializing) return false;
        const hasValue = formik.values && formik.values[name] !== undefined && String(formik.values[name]).trim() !== '';
        const show = (formik.touched[name] || formik.submitCount > 0 || hasValue) && !!formik.errors[name];
        return show;
    };

    const getFormErrorMessage = (name) => {
        if (initializing) return null;
        const hasValue = formik.values && formik.values[name] !== undefined && String(formik.values[name]).trim() !== '';
        const show = (formik.touched[name] || formik.submitCount > 0 || hasValue) && formik.errors[name];
        return show ? <small className="p-error">{formik.errors[name]}</small> : null;
    };

    const isFormValid = () => Object.keys(formik.errors).length === 0;

    // Enable submit button when all required fields are filled (lightweight check)
    const requiredFieldsFilled = () => {
        const vals = formik.values || {};
        if (editable) {
            // For update: require only name and role to be present (password hidden/not required)
            const basic = vals.fullName && String(vals.fullName).trim() !== '' && vals.roleID && String(vals.roleID).toString().trim() !== '';
            return !!basic;
        }
        const allPresent =
            vals.fullName &&
            String(vals.fullName).trim() !== '' &&
            vals.roleID &&
            String(vals.roleID).toString().trim() !== '' &&
            vals.cnic &&
            String(vals.cnic).trim() !== '' &&
            vals.mobileNumber &&
            String(vals.mobileNumber).replace(/\D+/g, '').length === 11 &&
            vals.email &&
            String(vals.email).trim() !== '' &&
            vals.password &&
            String(vals.password).trim() !== '';
        return !!allPresent;
    };

    const getZones = async () => {
        const response = await handleGetRequest('Zone/getAllZones', false);
        if (response?.success === true) {
            setZonesList(response?.data);
        }
    };

    useEffect(() => {
        getZones();
    }, []);

    return (
        <>
            <form onSubmit={formik.handleSubmit} className="p-fluid p-mt-2">
                <div className="p-fluid p-formgrid grid mb-5 col-12 md:col-12">
                    <div className="p-fluid p-formgrid grid col-12 md:col-6">
                        <div className="p-field col-12 md:col-12 pt-3">
                            <div className="p-field">
                                <label htmlFor="fullName" className={classNames({ 'p-erro': isFormFieldValid('fullName') }, 'Label__Text')}>
                                    Name <span className="Label__Required">*</span>
                                </label>
                                <InputText id="fullName" name="fullName" value={formik?.values?.fullName || ''} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('fullName') }, 'Input__Round mt-1')} />
                                {getFormErrorMessage('fullName')}
                            </div>
                        </div>
                    </div>
                    <div className="p-fluid p-formgrid grid col-12 md:col-6">
                        <div className="p-field col-12 md:col-12 pt-3">
                            <div className="p-field">
                                <label htmlFor="cnic" className={classNames({ 'p-erro': isFormFieldValid('cnic') }, 'Label__Text')}>
                                    Cnic <span className="Label__Required">*</span>
                                </label>
                                <InputText
                                    id="cnic"
                                    name="cnic"
                                    value={formik?.values?.cnic || ''}
                                    onChange={(e) => formik.setFieldValue('cnic', String(e.target.value).replace(/\D+/g, ''))}
                                    className={classNames({ 'p-invalid': isFormFieldValid('cnic') }, 'Input__Round mt-1')}
                                    keyfilter="int"
                                    maxLength={13}
                                />
                                {getFormErrorMessage('cnic')}
                            </div>
                        </div>
                    </div>
                    <div className="p-fluid p-formgrid grid col-12 md:col-6">
                        <div className="p-field col-12 md:col-12 pt-3">
                            <div className="p-field">
                                <label htmlFor="mobileNumber" className={classNames({ 'p-erro': isFormFieldValid('mobileNumber') }, 'Label__Text')}>
                                    Mobile Number <span className="Label__Required">*</span>
                                </label>
                                <InputText
                                    maxLength="11"
                                    minLength="11"
                                    keyfilter="int"
                                    id="mobileNumber"
                                    name="mobileNumber"
                                    value={formik?.values?.mobileNumber || ''}
                                    onChange={formik.handleChange}
                                    className={classNames({ 'p-invalid': isFormFieldValid('mobileNumber') }, 'Input__Round mt-1')}
                                />
                                {getFormErrorMessage('mobileNumber')}
                            </div>
                        </div>
                    </div>
                    <div className="p-fluid p-formgrid grid col-12 md:col-6">
                        <div className="p-field col-12 md:col-12 pt-3">
                            <div className="p-field">
                                <label htmlFor="email" className={classNames({ 'p-erro': isFormFieldValid('email') }, 'Label__Text')}>
                                    Email <span className="Label__Required">*</span>
                                </label>
                                <InputText maxLength={50} type="email" id="email" name="email" value={formik?.values?.email || ''} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('email') }, 'Input__Round mt-1')} />
                                {getFormErrorMessage('email')}
                            </div>
                        </div>
                    </div>
                    <div className="p-fluid p-formgrid grid col-12 md:col-6">
                        <div className="p-field col-12 md:col-12 pt-3">
                            <div className="p-field">
                                <label htmlFor="password" className={classNames({ 'p-erro': isFormFieldValid('password') }, 'Label__Text')}>
                                    Password {editable ? <span className="Label__Optional">(Optional)</span> : <span className="Label__Required">*</span>}
                                </label>
                                <InputText
                                    type="password"
                                    id="password"
                                    name="password"
                                    minLength={8}
                                    value={formik?.values?.password || ''}
                                    // onChange={formik.handleChange}
                                    onChange={(e) => {
                                        const valueWithoutSpaces = e.target.value.replace(/\s/g, '');
                                        formik.setFieldValue('password', valueWithoutSpaces);
                                    }}
                                    className={classNames({ 'p-invalid': isFormFieldValid('password') }, 'Input__Round mt-1')}
                                />
                                {getFormErrorMessage('password')}
                            </div>
                        </div>
                    </div>
                    <div className="p-fluid p-formgrid grid col-12 md:col-6">
                        <div className="p-field col-12 md:col-12 pt-3">
                            <div className="p-field">
                                <label htmlFor="roleID" className={classNames({ 'p-erro': isFormFieldValid('roleID') }, 'Label__Text')}>
                                    Role <span className="Label__Required">*</span>
                                </label>
                                <Dropdown
                                    placeholder="--Select--"
                                    options={rolesList}
                                    optionValue="id"
                                    optionLabel="name"
                                    id="roleID"
                                    name="roleID"
                                    value={formik?.values?.roleID || ''}
                                    onChange={formik.handleChange}
                                    className={classNames({ 'p-invalid': isFormFieldValid('roleID') }, 'Dropdown__Round mt-1')}
                                />
                                {getFormErrorMessage('roleID')}
                            </div>
                        </div>
                    </div>
                    {formik?.values.roleID === 2 && (
                        <div className="p-fluid p-formgrid grid col-12 md:col-6">
                            <div className="p-field col-12 md:col-12 pt-3">
                                <div className="p-field">
                                    <label htmlFor="zoneId" className={classNames({ 'p-erro': isFormFieldValid('zoneId') }, 'Label__Text')}>
                                        Zone <span className="Label__Required">*</span>
                                    </label>
                                    <Dropdown
                                        placeholder="--Select--"
                                        options={zonesList}
                                        optionValue="zoneID"
                                        optionLabel="zoneName"
                                        id="zoneId"
                                        name="zoneId"
                                        value={formik?.values?.zoneId || ''}
                                        onChange={formik.handleChange}
                                        className={classNames({ 'p-invalid': isFormFieldValid('zoneId') }, 'Dropdown__Round mt-1')}
                                    />
                                    {getFormErrorMessage('zoneId')}
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="p-fluid p-formgrid grid col-12 md:col-6">
                        <div className="p-field pt-3">
                            <label className="Label__Text">
                                Status <span className="Label__Required">*</span>
                            </label>

                            <div className="flex align-items-center gap-4 mt-3">
                                <div className="flex align-items-center gap-2">
                                    <RadioButton inputId="active" name="status" value={true} onChange={(e) => formik.setFieldValue('status', e.value)} checked={formik.values.status === true} />
                                    <label htmlFor="active">Active</label>
                                </div>

                                <div className="flex align-items-center gap-2">
                                    <RadioButton inputId="inactive" name="status" value={false} onChange={(e) => formik.setFieldValue('status', e.value)} checked={formik.values.status === false} />
                                    <label htmlFor="inactive">Inactive</label>
                                </div>
                            </div>
                            {getFormErrorMessage('status')}
                        </div>
                    </div>
                    <div className="p-fluid p-formgrid grid col-12 md:col-12 mt-4">
                        <div className="flex justify-content-center w-full">
                            <Button label="Cancel" className="p-button-secondary Black__Btn" style={{ width: '100px' }} onClick={() => onHideUserDialog(false)} type="button" />
                            <Button label={editable ? 'Update' : 'Add'} className="p-button-primary Btn__Dark" style={{ width: '100px' }} type="submit"  />
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
}

export default AddUserDialog;
